import React, { useState } from 'react';
import { SearchIcon, StarIcon, TrashIcon, ArchiveIcon, ReplyIcon, CornerUpRightIcon, PlusIcon, MailIcon } from 'lucide-react';
// Mock email data
const mockEmails = [{
  id: 1,
  from: 'John Smith',
  email: 'john.smith@example.com',
  subject: 'Rate Quote Request',
  preview: 'Hello, I would like to inquire about current mortgage rates for a 30-year fixed loan...',
  body: 'Hello,\n\nI would like to inquire about current mortgage rates for a 30-year fixed loan. I am looking to purchase a home in the $450,000 range with a 20% down payment. My credit score is around 760.\n\nCould you please provide me with the current rates and any special programs that might be available?\n\nThank you,\nJohn Smith',
  date: '10:30 AM',
  read: false,
  starred: false,
  attachments: [],
  labels: ['Client', 'Important']
}, {
  id: 2,
  from: 'Sarah Johnson',
  email: 'sarah.johnson@example.com',
  subject: 'Document Submission',
  preview: 'I have attached the requested documents for my mortgage application...',
  body: 'Hi there,\n\nI have attached the requested documents for my mortgage application. Please find attached:\n\n1. Last 2 years of tax returns\n2. Recent pay stubs\n3. Bank statements for the last 3 months\n4. Proof of additional income\n\nPlease let me know if you need anything else from me.\n\nBest regards,\nSarah Johnson',
  date: 'Yesterday',
  read: true,
  starred: true,
  attachments: [{
    name: 'tax_returns_2022.pdf',
    size: '2.4 MB'
  }, {
    name: 'paystubs_recent.pdf',
    size: '1.1 MB'
  }, {
    name: 'bank_statements.pdf',
    size: '3.7 MB'
  }],
  labels: ['Client', 'Documents']
}, {
  id: 3,
  from: 'Michael Davis',
  email: 'michael.davis@example.com',
  subject: 'Pre-approval Request',
  preview: 'I am interested in getting pre-approved for a mortgage. My wife and I are first-time homebuyers...',
  body: 'Hello,\n\nI am interested in getting pre-approved for a mortgage. My wife and I are first-time homebuyers looking in the Seattle area. We have been saving for a down payment and have about $60,000 ready.\n\nOur combined income is approximately $150,000 per year, and we both have credit scores above 740. We are hoping to find a home in the $500,000 - $600,000 range.\n\nCould you please let us know what information you need from us to start the pre-approval process?\n\nThank you,\nMichael Davis',
  date: 'Feb 10',
  read: true,
  starred: false,
  attachments: [],
  labels: ['Lead']
}, {
  id: 4,
  from: 'Jennifer Wilson',
  email: 'jennifer.wilson@example.com',
  subject: 'Refinance Inquiry',
  preview: 'I am considering refinancing my current mortgage to take advantage of lower rates...',
  body: 'Good afternoon,\n\nI am considering refinancing my current mortgage to take advantage of lower rates. I purchased my home in 2018 with a 30-year fixed rate at 4.5%.\n\nThe original loan amount was $320,000, and I believe my home has appreciated to around $400,000 based on recent sales in my neighborhood.\n\nCould you please let me know what rates you could offer for a refinance? I am also interested in potentially taking some cash out to fund a home renovation project.\n\nThank you for your help,\nJennifer Wilson',
  date: 'Feb 8',
  read: false,
  starred: true,
  attachments: [],
  labels: ['Client', 'Refinance']
}, {
  id: 5,
  from: 'Robert Brown',
  email: 'robert.brown@example.com',
  subject: 'Application Status',
  preview: 'I submitted my mortgage application last week and was wondering about the current status...',
  body: 'Hi,\n\nI submitted my mortgage application last week (reference #APP-2023-0568) and was wondering about the current status. The online portal shows it as "In Review," but I wanted to check if there are any updates or if you need any additional information from me.\n\nI am hoping to close by the end of next month if possible.\n\nThanks,\nRobert Brown',
  date: 'Feb 5',
  read: true,
  starred: false,
  attachments: [],
  labels: ['Client', 'Application']
}];
export function InboxPage() {
  const [emails, setEmails] = useState(mockEmails);
  const [selectedEmail, setSelectedEmail] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const selectedEmailData = emails.find(email => email.id === selectedEmail);
  const filteredEmails = emails.filter(email => {
    return email.subject.toLowerCase().includes(searchQuery.toLowerCase()) || email.from.toLowerCase().includes(searchQuery.toLowerCase()) || email.body.toLowerCase().includes(searchQuery.toLowerCase());
  });
  const markAsRead = (id: number) => {
    setEmails(emails.map(email => email.id === id ? {
      ...email,
      read: true
    } : email));
    setSelectedEmail(id);
  };
  const toggleStar = (id: number, event: React.MouseEvent) => {
    event.stopPropagation();
    setEmails(emails.map(email => email.id === id ? {
      ...email,
      starred: !email.starred
    } : email));
  };
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };
  return <div className="h-full" data-id="element-1363">
      <div className="flex justify-between items-center mb-6" data-id="element-1364">
        <h1 className="text-2xl font-bold" data-id="element-1365">Email Inbox</h1>
        <div className="space-x-2" data-id="element-1366">
          <button className="btn btn-primary" data-id="element-1367">
            <PlusIcon className="h-4 w-4 mr-1" data-id="element-1368" />
            Compose
          </button>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow overflow-hidden flex h-[calc(100vh-220px)]" data-id="element-1369">
        {/* Sidebar */}
        <div className="w-64 border-r border-neutral-200 flex flex-col" data-id="element-1370">
          {/* Search */}
          <div className="p-3 border-b border-neutral-200" data-id="element-1371">
            <div className="relative" data-id="element-1372">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3" data-id="element-1373">
                <SearchIcon className="h-4 w-4 text-neutral-400" data-id="element-1374" />
              </div>
              <input type="text" className="w-full rounded-md border border-neutral-300 py-1.5 pl-10 pr-3 text-sm placeholder-neutral-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500" placeholder="Search emails..." value={searchQuery} onChange={handleSearch} data-id="element-1375" />
            </div>
          </div>
          {/* Email list header */}
          <div className="p-3 border-b border-neutral-200 flex items-center justify-between" data-id="element-1376">
            <span className="text-sm font-medium text-neutral-700" data-id="element-1377">Inbox</span>
            <div className="text-xs text-neutral-500" data-id="element-1378">
              {emails.filter(e => !e.read).length} unread
            </div>
          </div>
          {/* Email list */}
          <div className="flex-1 overflow-auto" data-id="element-1379">
            {filteredEmails.length === 0 ? <div className="p-4 text-center text-neutral-500" data-id="element-1380">
                No emails found
              </div> : filteredEmails.map(email => <div key={email.id} className={`p-3 border-b border-neutral-200 hover:bg-neutral-50 cursor-pointer ${!email.read ? 'bg-blue-50' : ''} ${selectedEmail === email.id ? 'bg-neutral-100' : ''}`} onClick={() => markAsRead(email.id)} data-id="element-1381">
                  <div className="flex items-start" data-id="element-1382">
                    <button className="mr-2 text-neutral-400 hover:text-yellow-400" onClick={e => toggleStar(email.id, e)} data-id="element-1383">
                      <StarIcon className={`h-4 w-4 ${email.starred ? 'text-yellow-400 fill-yellow-400' : ''}`} data-id="element-1384" />
                    </button>
                    <div className="flex-1 min-w-0" data-id="element-1385">
                      <div className="flex items-center justify-between" data-id="element-1386">
                        <p className={`text-sm truncate ${!email.read ? 'font-medium' : ''}`} data-id="element-1387">
                          {email.from}
                        </p>
                        <p className="text-xs text-neutral-500 whitespace-nowrap ml-2" data-id="element-1388">
                          {email.date}
                        </p>
                      </div>
                      <p className="text-sm font-medium truncate" data-id="element-1389">
                        {email.subject}
                      </p>
                      <p className="text-xs text-neutral-500 truncate" data-id="element-1390">
                        {email.preview}
                      </p>
                      {email.attachments.length > 0 && <div className="mt-1 flex items-center" data-id="element-1391">
                          <span className="text-xs text-neutral-500" data-id="element-1392">
                            {email.attachments.length} attachment
                            {email.attachments.length > 1 ? 's' : ''}
                          </span>
                        </div>}
                    </div>
                  </div>
                </div>)}
          </div>
        </div>
        {/* Email content */}
        <div className="flex-1 flex flex-col" data-id="element-1393">
          {selectedEmailData ? <>
              {/* Email header */}
              <div className="p-4 border-b border-neutral-200" data-id="element-1394">
                <div className="flex justify-between items-start mb-3" data-id="element-1395">
                  <h2 className="text-xl font-medium" data-id="element-1396">
                    {selectedEmailData.subject}
                  </h2>
                  <div className="flex space-x-2" data-id="element-1397">
                    <button className="text-neutral-500 hover:text-neutral-700 p-1 rounded-full hover:bg-neutral-100" data-id="element-1398">
                      <ArchiveIcon className="h-5 w-5" data-id="element-1399" />
                    </button>
                    <button className="text-neutral-500 hover:text-neutral-700 p-1 rounded-full hover:bg-neutral-100" data-id="element-1400">
                      <TrashIcon className="h-5 w-5" data-id="element-1401" />
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between" data-id="element-1402">
                  <div className="flex items-center" data-id="element-1403">
                    <div className="h-10 w-10 rounded-full bg-neutral-200 flex items-center justify-center mr-3" data-id="element-1404">
                      <span className="text-neutral-600 font-medium" data-id="element-1405">
                        {selectedEmailData.from.charAt(0)}
                      </span>
                    </div>
                    <div data-id="element-1406">
                      <div className="flex items-center" data-id="element-1407">
                        <p className="font-medium" data-id="element-1408">{selectedEmailData.from}</p>
                        <span className="mx-2 text-neutral-400" data-id="element-1409">•</span>
                        <p className="text-sm text-neutral-500" data-id="element-1410">
                          {selectedEmailData.date}
                        </p>
                      </div>
                      <p className="text-sm text-neutral-500" data-id="element-1411">
                        {selectedEmailData.email}
                      </p>
                    </div>
                  </div>
                  <div className="flex space-x-2" data-id="element-1412">
                    <button className="text-neutral-500 hover:text-neutral-700 p-1 rounded-full hover:bg-neutral-100" data-id="element-1413">
                      <ReplyIcon className="h-5 w-5" data-id="element-1414" />
                    </button>
                    <button className="text-neutral-500 hover:text-neutral-700 p-1 rounded-full hover:bg-neutral-100" data-id="element-1415">
                      <CornerUpRightIcon className="h-5 w-5" data-id="element-1416" />
                    </button>
                  </div>
                </div>
              </div>
              {/* Email body */}
              <div className="p-4 flex-1 overflow-auto" data-id="element-1417">
                <div className="whitespace-pre-line text-neutral-800" data-id="element-1418">
                  {selectedEmailData.body}
                </div>
                {/* Attachments */}
                {selectedEmailData.attachments.length > 0 && <div className="mt-6 border-t border-neutral-200 pt-4" data-id="element-1419">
                    <h3 className="text-sm font-medium mb-3" data-id="element-1420">
                      Attachments ({selectedEmailData.attachments.length})
                    </h3>
                    <div className="grid grid-cols-2 gap-3" data-id="element-1421">
                      {selectedEmailData.attachments.map((attachment, index) => <div key={index} className="border border-neutral-200 rounded-md p-3 flex items-center" data-id="element-1422">
                            <div className="h-10 w-10 bg-neutral-100 rounded flex items-center justify-center mr-3" data-id="element-1423">
                              <span className="text-neutral-500 text-xs" data-id="element-1424">
                                .PDF
                              </span>
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-1425">
                              <p className="text-sm font-medium truncate" data-id="element-1426">
                                {attachment.name}
                              </p>
                              <p className="text-xs text-neutral-500" data-id="element-1427">
                                {attachment.size}
                              </p>
                            </div>
                          </div>)}
                    </div>
                  </div>}
              </div>
              {/* Reply box */}
              <div className="p-4 border-t border-neutral-200" data-id="element-1428">
                <div className="border border-neutral-300 rounded-md p-3" data-id="element-1429">
                  <div className="text-sm text-neutral-500 mb-2" data-id="element-1430">
                    Reply to {selectedEmailData.from}
                  </div>
                  <textarea className="w-full border-0 focus:ring-0 p-0 text-neutral-800 placeholder-neutral-400 resize-none" placeholder="Type your reply here..." rows={3} data-id="element-1431"></textarea>
                  <div className="flex justify-between items-center mt-3 pt-3 border-t border-neutral-200" data-id="element-1432">
                    <div data-id="element-1433">
                      <button className="text-neutral-500 hover:text-neutral-700 p-1 rounded-full hover:bg-neutral-100" data-id="element-1434">
                        <span className="sr-only" data-id="element-1435">Attach file</span>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-1436">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" data-id="element-1437" />
                        </svg>
                      </button>
                    </div>
                    <button className="btn btn-primary py-1.5 px-3 text-sm" data-id="element-1438">
                      Send
                    </button>
                  </div>
                </div>
              </div>
            </> : <div className="flex-1 flex flex-col items-center justify-center text-neutral-500" data-id="element-1439">
              <MailIcon className="h-12 w-12 mb-4" data-id="element-1440" />
              <p className="text-lg" data-id="element-1441">Select an email to view</p>
              <p className="text-sm" data-id="element-1442">No email selected</p>
            </div>}
        </div>
      </div>
    </div>;
}